import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import axiosBaseQuery from 'services/axiosBaseQuery'
import {
  AddInventoryRequest,
  ChangeAmountRequest,
  InventoryItemRequest,
  InventoryItemResponse,
  InventoryListRequest,
  InventoryListResponse,
  MutationResponse,
  TransactionListRequest,
  ExportResponse,
  ExportRequest,
  BulkMutationResponse,
  BulkDeleteRequest,
  ChangeCapacityRequest,
  ConvertInventoryRequest,
  TransactionChartResponse,
  TransactionChartRequest,
  type TargetListRequest,
  type TargetListResponse,
  type AddTargetRequest,
  type DeleteTargetRequest,
  TargetDetailsResponse,
} from './types'
import { sendExportNotification } from 'utils'
import { reportsApi } from 'services/ReportManagement/reports'
import { GenericResponse } from 'services/types'

export const inventoryApi = createApi({
  reducerPath: 'inventoryApi',
  tagTypes: [
    'Inventory',
    'InventoryDetails',
    'Transactions',
    'TransactionChart',
    'TargetList',
    'TargetDetails',
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/MRF/'}`,
  }),
  endpoints: (builder) => ({
    getInventoryList: builder.query<InventoryListResponse, InventoryListRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/inventoryitem/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['Inventory'],
    }),
    getInventoryDetail: builder.query<InventoryItemResponse, InventoryItemRequest>({
      query: ({ clientId, inventoryItemId }) => ({
        url: `v2/inventory_item/clients/${clientId}/?inventory_item_id=${inventoryItemId}`,
        method: 'GET',
      }),
      providesTags: ['InventoryDetails'],
    }),
    addInventoryRecord: builder.mutation<MutationResponse, AddInventoryRequest>({
      query: ({ clientId, body }) => ({
        url: `clients/${clientId}/inventoryitem/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Inventory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    increaseAmount: builder.mutation<MutationResponse, ChangeAmountRequest>({
      query: ({ clientId, materialId, ...data }) => ({
        url: `clients/${clientId}/material/${materialId}/increase/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Inventory', 'InventoryDetails', 'Transactions', 'TransactionChart'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    decreaseAmount: builder.mutation<MutationResponse, ChangeAmountRequest>({
      query: ({ clientId, materialId, ...data }) => ({
        url: `clients/${clientId}/material/${materialId}/decrease/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Inventory', 'InventoryDetails', 'Transactions', 'TransactionChart'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    convertInventory: builder.mutation<MutationResponse, ConvertInventoryRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/inventoryitem/convert/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Inventory', 'InventoryDetails', 'Transactions', 'TransactionChart'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    changeCapacity: builder.mutation<MutationResponse, ChangeCapacityRequest>({
      query: ({ clientId, id, ...data }) => ({
        url: `clients/${clientId}/inventoryitem/${id}/capacity/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Inventory', 'InventoryDetails', 'TransactionChart'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteInventoryItem: builder.mutation<BulkMutationResponse, BulkDeleteRequest<number, string>>({
      query: ({ clientId, deleteIdArray: inventory_item_id_list }) => ({
        url: `clients/${clientId}/inventoryitem/bulk_delete/`,
        method: 'DELETE',
        data: { inventory_item_id_list },
        errorOverride: true,
      }),
      invalidatesTags: ['Inventory', 'Transactions', 'TransactionChart'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
          sendNotification({
            type: 'error',
            toastContent: (err as any).error.data.failure_list?.[0]?.description,
          })
        }
      },
    }),
    exportInventoryList: builder.mutation<ExportResponse, ExportRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/inventoryitem/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (err) {}
      },
    }),
    getTransactionList: builder.query<InventoryListResponse, TransactionListRequest>({
      query: ({ client_id, inventory_id, ...data }) => ({
        url: `clients/${client_id}/inventoryitem/${inventory_id}/transaction_list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['Transactions'],
    }),
    getTransactionChart: builder.query<TransactionChartResponse, TransactionChartRequest>({
      query: ({ client_id, inventoryId, end_date, start_date }) => ({
        url: `clients/${client_id}/inventoryitem/${inventoryId}/inventory_chart/?start_date=${start_date}&end_date=${end_date}`,
        method: 'GET',
      }),
      providesTags: ['TransactionChart'],
    }),
    getTargetList: builder.query<TargetListResponse, TargetListRequest>({
      query: ({ clientId, material_id, ...data }) => ({
        url: `clients/${clientId}/target/list/`,
        method: 'POST',
        data,
        params: {
          material_id,
        },
      }),
      providesTags: ['TargetList'],
    }),
    addTarget: builder.mutation<MutationResponse, AddTargetRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/target/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {}
      },
      invalidatesTags: ['TargetList'],
    }),
    editTarget: builder.mutation<GenericResponse, AddTargetRequest & { targetId: number }>({
      query: ({ clientId, targetId, ...data }) => ({
        url: `clients/${clientId}/target/${targetId}/`,
        method: 'PUT',
        data,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {}
      },
      invalidatesTags: ['TargetList', 'TargetDetails'],
    }),
    getTarget: builder.query<TargetDetailsResponse, { clientId: number; targetId: number }>({
      query: ({ clientId, targetId }) => ({
        url: `clients/${clientId}/target/${targetId}/`,
        method: 'GET',
      }),
      providesTags: ['TargetDetails'],
    }),
    deleteTarget: builder.mutation<MutationResponse, DeleteTargetRequest>({
      query: ({ clientId, targetId }) => ({
        url: `clients/${clientId}/target/${targetId}/`,
        method: 'DELETE',
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {}
      },
      invalidatesTags: ['TargetList'],
    }),
  }),
})

export const {
  useGetInventoryListQuery,
  useAddInventoryRecordMutation,
  useGetInventoryDetailQuery,
  useDecreaseAmountMutation,
  useIncreaseAmountMutation,
  useDeleteInventoryItemMutation,
  useExportInventoryListMutation,
  useChangeCapacityMutation,
  useConvertInventoryMutation,
  useGetTransactionListQuery,
  useGetTransactionChartQuery,
  useGetTargetListQuery,
  useAddTargetMutation,
  useDeleteTargetMutation,
  useEditTargetMutation,
  useGetTargetQuery,
} = inventoryApi
